import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import FeedbackForm from "../components/FeedbackForm";
import { useAuth } from "../contexts/authContext";
import { Line } from "react-chartjs-2";
import { Chart, LineElement, PointElement, LinearScale, CategoryScale } from 'chart.js';
import { fetchWithAuth } from "../utils/fetchWithAuth";

Chart.register(LineElement, PointElement, LinearScale, CategoryScale);

export default function BrainAgePage() {
  const [questions, setQuestions] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [userNickname, setUserNickname] = useState('');
  const { isLoggedIn, setIsLoggedIn, userEmail, setUserEmail } = useAuth();
  const navigate = useNavigate();
  const [visiblePassages, setVisiblePassages] = useState({});
  const [remainingCalls, setRemainingCalls] = useState(null);
  const [timeLeft, setTimeLeft] = useState(100);
  const [resultShown, setResultShown] = useState(false); // 결과 보기 눌렀는지 여부

  // 1. 남은 호출 횟수 불러오기
  useEffect(() => {
    const token = localStorage.getItem("token");
    const anonId = localStorage.getItem("anon_id");

    fetchWithAuth(`/gpt-usage`, {
      headers: {
        "X-Anonymous-ID": !token && anonId ? anonId : "",
      },
    })
      .then((res) => res.json())
      .then((data) => setRemainingCalls(data.remaining))
      .catch((err) => console.error("사용 가능 횟수 조회 실패", err));
  }, []);

  useEffect(() => {
    if (questions.length === 0) return; // 문제 세팅 전에는 타이머 시작 안 함

    if (resultShown || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setQuestions(prev =>
          prev.map(q =>
            q.userAnswer
              ? q
              : { ...q, userAnswer: "__TIMEOUT__" }
          )
        );
      }
      return;
    }

    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [timeLeft, questions.length, resultShown]);

  const handleStart = () => {
    if (remainingCalls === null) {
      alert("남은 사용 가능 횟수를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }
    if (remainingCalls < 3) {
      alert("테스트를 시작하려면 최소 3회의 사용 가능 횟수가 필요합니다.");
      return;
    }
    fetchQuiz();
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetchWithAuth(`/brain-trainer/brain-age-history`, {
    })
      .then(res => res.json())
      .then(data => {
        // 👇 배열인지 확인하고 저장
        if (Array.isArray(data)) {
          setHistoryData(data);
        } else {
          console.warn("❗ 응답이 배열이 아님:", data);
          setHistoryData([]);  // fallback
        }
      });
  }, []);

  useEffect(() => {
    const nickname = localStorage.getItem("nickname");
    if (nickname) setUserNickname(nickname);
  }, []);

  const fetchQuiz = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const anonId = localStorage.getItem("anon_id");
      const res = await fetchWithAuth(`/brain-trainer/brain-age-quiz`, {
        method: "POST",
        headers: {
          "X-Anonymous-ID": !token && anonId ? anonId : "",  // 비회원일 때만 헤더 추가
        },
      })
      if (res.status === 429) {
        alert("오늘의 사용 가능 횟수를 모두 소진하셨습니다.");
        setLoading(false);
        return;
      }
      const data = await res.json();
      const withMeta = data.questions.map(q => ({
        ...q,
        type: q.type || q.category,
        question: q.question || q.text,
        userAnswer: null
      }));
      setQuestions(withMeta);
      // ✅ 테스트는 3회 차감
      setRemainingCalls((prev) => Math.max(0, prev - 3));
    } catch (err) {
      console.error("뇌 나이 퀴즈 생성 실패", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("nickname");
    localStorage.removeItem("usage_count");
    localStorage.removeItem("daily_limit");
    setIsLoggedIn(false);
    setUserEmail("");
    navigate("/login");
  };

  const submit = async () => {
    const results = questions.map((q) => ({
      type: q.type,
      difficulty: "보통",
      correct: q.userAnswer === q.answer ? 1 : 0,
      total: 1,
    }));

    const res = await fetchWithAuth(`/brain-trainer/brain-age-result`, {
      method: "POST",
      body: JSON.stringify({ results }),
    });
    const data = await res.json();
    const brainAge = data.brain_age;
    setResult(brainAge);

    if (isLoggedIn) {
      await fetchWithAuth(`/brain-trainer/save-brain-age-result`, {
        method: "POST",
        body: JSON.stringify({
          brain_age: brainAge,
          accuracy: results.filter(r => r.correct).length / results.length,
        }),
      });

      // 새 기록 저장 후 다시 히스토리 fetch
      const res2 = await fetchWithAuth(`/brain-trainer/brain-age-history`, {
      });
      const updated = await res2.json();
      setHistoryData(updated);
    }
  };

  const chartData = {
    labels: historyData.map(item => item.date),
    datasets: [
      {
        label: "🧠 뇌 나이",
        data: historyData.map(item => item.brain_age),
        borderColor: "rgba(75,192,192,1)",
        tension: 0.3,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-white">
      {/* 공통 헤더 */}
      <header className="w-full flex flex-col md:flex-row justify-between items-center px-4 py-4 pt-16 border-b bg-white dark:bg-gray-800">
        <div className="text-center md:text-left mb-4 md:mb-0">
          <h1 className="text-xl font-bold text-blue-600 dark:text-blue-300">두뇌트레이너</h1>
          <nav className="mt-2 text-sm">
            <Link to="/" className="hover:text-blue-400">메인페이지로 이동</Link>
            <span className="mx-2">|</span>
            <Link to="/brain-trainer" className="hover:text-blue-400">두뇌트레이너(Brain Trainer)</Link>
            <span className="mx-2">|</span>
          </nav>
        </div>

        <div className="flex flex-col items-end gap-2 mt-4 md:mt-0">
          {isLoggedIn && (
            <div className="text-sm font-semibold text-green-300 text-center md:text-right w-full">
              어서오세요, <span className="text-yellow-400">{userNickname}</span> 님
            </div>
          )}
          <div className="flex gap-2">
            {isLoggedIn ? (
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-semibold bg-red-500 text-white rounded-full">
                Sign out
              </button>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-semibold bg-blue-500 text-white rounded-full">
                Sign in
              </Link>
            )}
             {!isLoggedIn && (
              <Link
                to="/signup"
                className="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-full"
              >
                Sign up
              </Link>
            )}
            {isLoggedIn && (
              <Link
                to="/mypage"
                className="px-4 py-2 text-sm font-semibold text-white bg-green-500 rounded-full"
              >
                MyPage
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* 본문 */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h2 className="text-xl font-bold text-center mb-4">🧠 뇌 나이 측정</h2>
          <p className="text-sm text-yellow-300 mb-6 font-semibold text-center">
            📌 오늘 남은 사용 가능 횟수(count): {remainingCalls ?? "로딩 중..."}회
          </p>

        <div className="fixed top-0 left-0 w-full text-2xl bg-gray-900 text-center py-4 z-50 shadow-lg">
          ⏱ {timeLeft}초
        </div>

        {!questions.length && (
          <div className="text-center">
            <p className="text-sm text-gray-300 mt-4">※ 총 12문제(보통10/어려움2)가 생성되며, <strong>문제 생성 시 약 1분가량 소요되므로 시작하기 클릭 후 잠시 대기</strong>해 주시기 바랍니다.</p>
            <p className="text-sm text-gray-300 mt-4">※ 뇌 나이 측정 테스트 시 사용 가능 횟수가 3회 차감되며, 로그인 후 테스트를 진행하는 경우 마이페이지에서 테스트 기록 확인이 가능합니다.</p>
            <br/>
            <button
              onClick={handleStart}
              disabled={loading}
              className="px-6 py-2 bg-blue-500 rounded-lg hover:bg-blue-600"
            >
              {loading ? "문제 생성 중..." : "시작하기"}
            </button>
          </div>
        )}

        {questions.length > 0 && (
          <div className="space-y-6">
            {questions.map((q, i) => (
              <div key={i} className="bg-gray-800 p-4 mb-4 rounded-lg shadow">
                {q.type === "이야기 기억력" && (
                  <>
                    {!visiblePassages[i] ? (
                      <button
                        onClick={() => {
                          setVisiblePassages(prev => ({ ...prev, [i]: true }));
                          setTimeout(() => {
                            setVisiblePassages(prev => ({ ...prev, [i]: false }));
                          }, 5000);
                        }}
                        className="text-sm text-yellow-300 underline mb-2"
                      >
                        📝 지문 보기 (5초간)
                      </button>
                    ) : (
                      <div className="text-sm text-gray-300 mb-2 italic">
                        📝 지문: {q.passage}
                      </div>
                    )}
                  </>
                )}
                <p className="mb-1 font-semibold">{i + 1}. {q.question}</p>

                {/* 유형 + 난이도 */}
                <p className="text-xs text-gray-400 mb-2 flex items-center gap-3">
                  <span>
                    🧩 유형:{" "}
                    <span className="text-blue-300">{q.type}</span>
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-semibold ${
                      q.difficulty === "어려움"
                        ? "bg-red-200 text-red-800"
                        : "bg-blue-200 text-blue-800"
                    }`}
                  >
                    난이도: {q.difficulty}
                  </span>
                </p>

                <div className="flex flex-col gap-2">
                  {q.choices.map((choice, j) => {
                    const isSelected = q.userAnswer === choice;
                    const isCorrect = choice === q.answer;
                    return (
                      <button
                        key={j}
                        className={`border px-4 py-2 rounded text-left ${
                          isSelected
                            ? isCorrect
                              ? "bg-green-600 border-green-600"
                              : "bg-red-600 border-red-600"
                            : "hover:bg-gray-700 border-gray-600"
                        }`}
                        onClick={() =>
                          setQuestions(prev =>
                            prev.map((x, idx) =>
                              idx === i ? { ...x, userAnswer: choice } : x
                            )
                          )
                        }
                        disabled={q.userAnswer !== null}
                      >
                        {choice}
                      </button>
                    );
                  })}
                </div>

                {q.userAnswer !== null && (
                  <p className="mt-2 text-sm">
                    {q.userAnswer === q.answer ? (
                      <span className="text-green-400">정답입니다!</span>
                    ) : q.userAnswer === "__TIMEOUT__" ? (
                      <span className="text-red-400">시간 초과! 정답: {q.answer}</span>
                    ) : (
                      <span className="text-red-400">오답입니다. 정답: {q.answer}</span>
                    )}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        {questions.length > 0 && questions.every(q => q.userAnswer !== null) && !result && (
          <div className="text-center mt-6">
            <button
              onClick={() => {
                setResultShown(true); // 타이머 정지
                submit();             // 결과 제출
              }}
              className="px-6 py-2 bg-green-500 hover:bg-green-600 rounded"
            >
              결과 확인하기
            </button>
          </div>
        )}

          {result && (
          <>
            <div className="mt-10 text-center text-xl font-bold text-yellow-400">
              🧠 당신의 뇌 나이는 <span className="text-blue-400">{result}세</span>입니다!
            </div>

            {historyData.length > 1 && (
              <div className="mt-10">
                <h3 className="text-lg font-semibold text-center mb-2">📊 나의 뇌 나이 변화</h3>
                <Line data={chartData} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
